import { useEffect, useState } from "react";
import { format } from "date-fns";
import logger from "../utils/logger";
import { formatTimeOfDay } from "@/utils/time-format";
import RaceCountdown from "./RaceCountdown";

export default function MarketStartTime({
  marketStartTime,
  raceId,
  hasHistoricalData,
  hasRaceResults = false,
  variant = "default", // "default" | "results" | "tips"
}) {
  const [showInterim, setShowInterim] = useState(false);

  if (!marketStartTime) return null;

  const marketStartDate = new Date(marketStartTime);
  const currentDate = new Date();

  // Get timezone from URL query params
  const getTimezoneFromQueryParams = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('timezone');
    }
    return null;
  };
  
  const timezone = getTimezoneFromQueryParams();
  
  // Site-wide time format: "1:10 pm" in Sydney time (or the ?timezone= zone).
  // normal-case keeps "pm" lowercase inside the upper-case race headers.
  const localTime = (
    <span className="normal-case">
      {formatTimeOfDay(marketStartDate, { timeZone: timezone || undefined })}
    </span>
  );

  // Set up interval to change "Started" to "Interim" after 5 minutes
  useEffect(() => {
    if (hasHistoricalData && !hasRaceResults && marketStartDate < currentDate) {
      // Calculate time elapsed since race start
      const minutesSinceStart = Math.floor(
        (currentDate - marketStartDate) / 60000,
      );

      if (minutesSinceStart >= 5) {
        // If already 5+ minutes since start, show interim immediately
        setShowInterim(true);
      } else {
        // Otherwise, set a timeout for the remaining time until 5 minutes
        const remainingMs = (5 - minutesSinceStart) * 60000;
        const timerId = setTimeout(() => {
          setShowInterim(true);
        }, remainingMs);

        return () => clearTimeout(timerId);
      }
    }
  }, [marketStartTime, hasHistoricalData, hasRaceResults]);

  if (hasRaceResults) {
    return <span className="type-8 ">Resulted @ {localTime}</span>;
  }

  // If we have historical data, it means the race has definitely run
  if (hasHistoricalData) {
    // "tips" variant for the RenderTips component
    if (variant === "tips") {
      return (
        <span className="type-8 ">
          {showInterim ? "Interim" : <>Started @ {localTime}</>}
        </span>
      );
    }

    if (variant === "results") {
      return (
        <div className="relative 9 overflow-hidden type-7">
          <span
            className="absolute w-full right-0 transition-all  duration-300 transform"
            style={{
              animation: "slideUpOut 10s infinite",
            }}
          >
            Resulted
          </span>
          <span
            className="absolute w-full right-0 transition-all  duration-300 transform"
            style={{
              animation: "slideUpIn 10s infinite",
            }}
          >
            @ {localTime}
          </span>
        </div>
      );
    }
  }

  // Check if the race is in the future
  if (marketStartDate > currentDate) {
    // Calculate minutes until race
    const minutesUntilRace = Math.floor(
      (marketStartDate - currentDate) / 60000,
    );

    // If less than 30 minutes until race
    if (minutesUntilRace <= 30) {
      // "tips" variant for the RenderTips component
      if (variant === "tips") {
        return (
          <span className="type-B">
            <RaceCountdown
              marketStartTime={marketStartTime}
              raceId={raceId}
              variant="jumps"
              showSeconds={minutesUntilRace < 1}
            />
          </span>
        );
      }

      if (variant === "results") {
        return (
          <div className="relative 9 overflow-hidden type-6">
            <RaceCountdown
              marketStartTime={marketStartTime}
              raceId={raceId}
              variant="jumps"
              showSeconds={minutesUntilRace < 1}
            />
          </div>
        );
      }

      return (
        <div className="relative h-9  overflow-hidden type-5">
          <span
            className="flex items-center justify-end w-full right-0 h-9  transition-all duration-300 transform"
            style={{
              animation: "slideUpOut 10s infinite",
            }}
          >
            @ {localTime}
          </span>
          <span
            className="absolute flex items-center justify-end w-full right-0 h-9  transition-all duration-300 transform"
            style={{
              animation: "slideUpIn 10s infinite",
            }}
          >
            <RaceCountdown
              marketStartTime={marketStartTime}
              raceId={raceId}
              variant="jumps"
              showSeconds={minutesUntilRace < 1}
            />
          </span>
        </div>
      );
    }

    // "tips" variant for the RenderTips component
    if (variant === "tips") {
      return <span className=" ">Jumps @ {localTime}</span>;
    }

    if (variant === "results") {
      return (
        <div className="relative text-right px-3 py-2 type-4">
          <span className="  text-right">Jumps @ {localTime}</span>
        </div>
      );
    }

    return (
      <div className="relative flex items-center justify-end h-9  text-right px-0 py-2 type-3">
        <span className="transition-all  duration-300">@ {localTime}</span>
      </div>
    );
  } else {
    if (hasHistoricalData) {
      return (
        <>
          <span className="type-A flex items-center justify-end w-full h-full right-0 transition-all  duration-300 transform">
            {hasRaceResults ? "Resulted" : showInterim ? "Interim" : "Started"}{" "}
            @ {localTime}
          </span>
        </>
      );
    } else {
      if (variant === "tips") {
        return (
          <>
            <span className="type-A flex items-center justify-end w-full h-full right-0 transition-all  duration-300 transform">
              {showInterim ? "Interim" : "Started"} @ {localTime}
            </span>
          </>
        );
      }
    }
  }

  // "tips" variant for the RenderTips component
  if (variant === "tips") {
    return (
      <>
        <span className="type-A flex items-center justify-end w-full h-full right-0 transition-all  duration-300 transform">
          {showInterim ? "Interim" : "Resulted"} @ {localTime}
        </span>
      </>
    );
  }

  if (variant === "results") {
    return (
      <div className="relative text-right px-3 py-2 type-2">
        <span className="text-right">Resulted</span>
      </div>
    );
  }

  // fail safe default render
  return (
    <div className="relative  h-9  overflow-hidden type-1">
      <span
        className="absolute flex items-center justify-end w-full h-full right-0 transition-all  duration-300 transform"
        style={{
          animation: "slideUpOut 5s infinite",
        }}
      >
        {showInterim ? "Interim" : "Resulted"}
      </span>
      <span
        className="absolute flex items-center justify-end w-full h-full right-0 transition-all  duration-300 transform"
        style={{
          animation: "slideUpIn 5s infinite",
        }}
      >
        @ {localTime}
      </span>
    </div>
  );
}
